// src/utils/tableSort.ts
/**
 * Client-side sorting for `CCMSDataTable`.
 *
 * ⚠️ **`CCMSDataTable` does not sort.** It tracks which header was clicked, draws the ↑/↓ arrow, and
 * calls `onSort({ field, direction })`. The **page** owns the sorting. A page that ignores the callback
 * gets a table whose arrows move and whose rows never do, which looks like a broken table rather than
 * missing code, and that is exactly what happened on Registrations and Enrolments.
 *
 * Sorting keys off the column's own **`accessor`**, the same function the table uses to render the cell.
 * That matters: the previous approach was a `switch` on the field name kept in parallel with the columns
 * array, and the two drifted. On the cases table `caseID` was marked `sortable: true` with no `case` in
 * the switch, so clicking it sorted every row by `''`; meanwhile `sessionCount` had a `case` but was
 * marked `sortable: false`. Deriving from the accessor makes both impossible.
 */
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';

/** Collator so "Case 10" lands after "Case 9", and case never decides order. */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const isBlank = (v: unknown) => v === null || v === undefined || v === '';

/**
 * Orders two cell values.
 *
 * Blanks always sink to the bottom **regardless of direction**, because "no value" is not smaller than
 * every value, it is absent. Flipping the direction should not float a column of empties to the top.
 */
export function compareValues(a: unknown, b: unknown): number {
  if (isBlank(a) && isBlank(b)) return 0;
  if (isBlank(a)) return 1;
  if (isBlank(b)) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();

  // Numeric strings compare as numbers, so "10" does not sort before "9". This also covers the SDK's
  // `yyyyMMddHHmmss` timestamps, which are numbers carried as strings.
  const an = Number(a);
  const bn = Number(b);
  if (!Number.isNaN(an) && !Number.isNaN(bn) && String(a).trim() !== '' && String(b).trim() !== '') {
    return an - bn;
  }

  return collator.compare(String(a), String(b));
}

/** Reads a cell value the same way `CCMSDataTable` does. */
function readCell<T>(row: T, column: TableColumn<T>): unknown {
  return typeof column.accessor === 'function'
    ? column.accessor(row)
    : (row as Record<string, unknown>)[column.accessor as string];
}

/**
 * Returns a sorted copy of `rows`, or `rows` untouched when there is nothing to sort by.
 *
 * @param columns the same array handed to `CCMSDataTable`, so the sort key is always the column's own
 *   accessor and cannot disagree with what the cell shows.
 */
export function sortRows<T>(
  rows: T[],
  columns: TableColumn<T>[],
  sort: SortConfig | null | undefined,
): T[] {
  if (!sort?.field) return rows;

  const column = columns.find((c) => c.key === sort.field);
  // An unknown field, or a column the page marked unsortable, leaves the order alone rather than
  // silently shuffling by a value nobody asked for.
  if (!column || column.sortable === false) return rows;

  const factor = sort.direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => compareValues(readCell(a, column), readCell(b, column)) * factor);
}
